/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';

import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination } from "swiper";

import FastlyPicture from '../../atoms/fastly-picture';
import ZoomSlide from '../../molecules/carousel-zoom';

// swiper core styles
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import "swiper/components/pagination/pagination.min.css";

import { logAmplitude } from '../../utils/amplitude';

const FASTLY_ON = process.env.NEXT_PUBLIC_FASTLY_ON === 'true';

const MobileCarouselWrapper = styled.div`
  grid-area: product-carousel;

  .swiper-pagination {
    bottom: 0;
  }
  
  .swiper-pagination-bullet {
    height: 1rem;
    width: 1rem;
    background-color: ${({theme }) => theme.color.bluePrimary};
    border-radius: 50%;
    display: inline-block;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: none;
  }
`;

const ImageContainer = styled.div`
  margin-bottom: 26px;
  img {
    width: 100%;
    height: auto;
  }
`;

SwiperCore.use([Pagination]);

const MobilePDPImageCarousel = ({ images, product, name, productType, setNoPinch }) => {
  const [swiper, setSwiper] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideImage, setSlideImage] = useState(
    images[0]?.product_large_url ?? `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg`
  );
  const [zoomClosed, setZoomClosed] = useState(true);
  const [zoomOpened, setZoomOpened] = useState(false);
  const [zoomAnimating, setZoomAnimating] = useState(false);
  const [originalStyle] = useState(global.window?.getComputedStyle(global.document?.body).overflowY) ?? 'auto';

  const handleOnSlideChange = (swiperEvent) => {
    // activeIndex supplied by Swiper
    const { activeIndex } = swiperEvent;
    let prevIndex = 0;

    if (images[activeIndex]) {
      // in order to track if the user swiped LEFT to view the next image
      // for the first time, use prevIndex and closure to ensure
      // the user is visiting the image for the 1st time
      if (activeIndex > prevIndex) {
        logAmplitude('PDP Interaction', {
          product,
          imagePosition: activeIndex + 1,
          interactionType: 'Product Image Swipe',
          image: images[activeIndex].product_url
        });

        prevIndex += 1;
      }

      setSlideImage(images[activeIndex].product_large_url);
      setSlideIndex(activeIndex);
    }
  };

  useEffect(() => {
    if (zoomOpened) global.document.body.style.overflowY = 'hidden';
    if (zoomClosed) global.document.body.style.overflowY = originalStyle;
  }, [zoomOpened, zoomClosed]);

  useEffect(() => {
    if (images.length === 0) {
      images.push({ product_zoom_url: `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg` });
    }

    setSlideImage(images[0].product_zoom_url);
  }, [images]);

  const handleOnZoomOpenClick = (imgIndex) => {
    global.document.body.style.overflowY = 'hidden';

    setSlideIndex(imgIndex);
    setSlideImage(images[imgIndex].product_zoom_url);
    setZoomAnimating(true);

    logAmplitude('PDP Interaction', {
      product,
      imagePosition: slideIndex + 1,
      interactionType: 'Product Image Click',
      image: images.product_zoom_url,
      imageZoomPosition: 'Click Image'
    });

    setTimeout(() => {
      setZoomAnimating(false);
      setZoomClosed(false);
      setZoomOpened(true);
    }, 0); /* parseInt(theme.animation.default, 10) */
  };

  const handleOnZoomCloseClick = () => {
    global.document.body.style.overflowY = '';

    setZoomAnimating(true);

    setTimeout(() => {
      setZoomAnimating(false);
      setZoomClosed(true);
      setZoomOpened(false);
    }, 0); /* parseInt(theme.animation.default, 10) */
  };

  const handleOnImageLoad = (event, imgIndex) => {
    const { performance } = global;
    const { currentTarget } = event;

    if (imgIndex > 0
      || !currentTarget
      || !performance
      || !performance.clearMarks
      || !performance.mark
    ) return;

    try {
      performance.clearMarks('PDP Image');
      performance.mark('PDP Image');
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const createAlt = (index) => `${name} - ${productType} - ${index + 1}`;
  return (
    <MobileCarouselWrapper>
      <Swiper
        modules={[Pagination]}
        pagination={{ clickable: true }}
        onSwiper={() => {
          setSwiper(swiper);
        }}
        onSlideChange={handleOnSlideChange}
        >
        {images.map((image, imgIndex) => (
          <SwiperSlide key={`productImage${imgIndex}`}>
            <ImageContainer
              width={image.attachment_width}
              height={image.attachment_height}
              onClick={() => handleOnZoomOpenClick(imgIndex)}
              onKeyDown={() => handleOnZoomOpenClick(imgIndex)}
            >
              {FASTLY_ON ? (
                <FastlyPicture
                  type="pdp"
                  src={image.product_large_url}
                  alt={image.alt ?? `${createAlt(imgIndex)}`}
                  onLoad={(e) => {
                    handleOnImageLoad(e, imgIndex);
                  }}
                />
              ) : (
                <img
                  src={image.product_large_url}
                  alt={image.alt ?? `${createAlt(imgIndex)}`}
                  onLoad={(e) => {
                    handleOnImageLoad(e, imgIndex);
                  }}
                />
              )}
            </ImageContainer>
          </SwiperSlide>
        ))}
      </Swiper>

      {zoomOpened && (
        <ZoomSlide
          images={images}
          image={slideImage}
          slideIndex={slideIndex}
          alt={createAlt(slideIndex)}
          onClose={handleOnZoomCloseClick}
          zoomAnimating={zoomAnimating}
          zoomClosed={zoomClosed}
          zoomOpened={zoomOpened}
          setNoPinch={setNoPinch}
          name={name}
          product={product}
        />
      )}
    </MobileCarouselWrapper>
  );
};

MobilePDPImageCarousel.defaultProps = {
    productType: 'product',
    setNoPinch: () => { },
    images: []
};

MobilePDPImageCarousel.propTypes = {
    product: PropTypes.object.isRequired,
    images: PropTypes.array,
    name: PropTypes.string.isRequired,
    productType: PropTypes.string,
    setNoPinch: PropTypes.func
};

export default MobilePDPImageCarousel;
